import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { companiesApi } from "@/lib/companies";
import { cn } from "@/lib/utils";

export interface CompanyPickerValue {
  id: number;
  companyName: string;
}

export interface CompanyPickerProps {
  value: CompanyPickerValue | null;
  onChange: (v: CompanyPickerValue | null) => void;
  placeholder?: string;
}

/**
 * There is no company-search endpoint, so this loads one generous page of
 * companies and filters client-side (cmdk's default CommandInput filtering).
 * Fine for a single-branch business with a bounded customer set; if the
 * customer list ever grows past a few hundred, add a /api/companies/finder
 * endpoint (backend, out of scope for this slice).
 */
export function CompanyPicker({ value, onChange, placeholder }: CompanyPickerProps) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["companies", "picker"],
    // deleted=false so soft-deleted companies never appear as selectable options; the
    // /api/companies list endpoint uses IgnoreQueryFilters and returns them otherwise.
    queryFn: () => companiesApi.list("page=0&pageSize=500&deleted=false"),
  });
  const companies = useMemo(() => data?.items ?? [], [data]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-label="Company"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="truncate">{value ? value.companyName : (placeholder ?? "Select company...")}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[300px]" align="start">
        <Command>
          <CommandInput placeholder="Search companies..." />
          <CommandList>
            <CommandEmpty>No companies found.</CommandEmpty>
            <CommandGroup>
              {companies.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.companyName}
                  onSelect={() => {
                    onChange({ id: c.id, companyName: c.companyName });
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4", value?.id === c.id ? "opacity-100" : "opacity-0")} />
                  {c.companyName}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
